'use client';

import 'react-loading-skeleton/dist/skeleton.css';

import { CardList, CardListProps } from '@sbercloud/uikit-product-site-cards';
import { Grid } from '@sbercloud/uikit-product-site-grid';
import { SectionBasic } from '@sbercloud/uikit-product-site-section';
import { LAYOUT_TYPE } from '@sbercloud/uikit-product-utils';
import { ButtonSimple } from '@snack-uikit/button';
import { InfoBlock } from '@snack-uikit/info-block';
import { useRouter } from 'next/navigation';
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { Layout } from 'src/layout';
import { HeroZero } from 'src/snack/components/HeroZero';
import { ClickAnalyticsType, DataFromFormInputs } from 'src/types';
import {
  EventCardType,
  EventCategory,
  EventsProps,
} from 'src/types/backendContent';

import s from './Events.module.scss';

const BREADCRUMBS = [
  {
    id: 'main',
    label: 'Главная',
    href: '/',
  },
  {
    id: 'events',
    label: 'Мероприятия',
  },
];

const HEADER = {
  title: 'Мероприятия Cloud.ru',
  text: 'Календарь наших событий: вебинары, конференции, хакатоны, лекции. Выбирайте интересную тему и регистрируйтесь на мероприятие или смотрите запись, когда вам будет удобно. ',
};

const BUTTON = {
  title: 'показать еще',
};

const PAST_EVENTS = {
  header: 'Прошедшие',
};

enum EventStatusesEnum {
  Active = 'ACTIVE',
  Stop = 'STOP',
}

function getTags({
  status,
  recordLink,
  category,
}: Pick<
  EventCardType,
  'status' | 'recordLink' | 'category'
>): CardListProps['tags'] {
  const videoLink = status === EventStatusesEnum.Active ? '' : recordLink;

  const mainCategory = category[0] as EventCategory | undefined;

  const badge =
    status === EventStatusesEnum.Active
      ? { title: 'Регистрация открыта' }
      : !videoLink
      ? { title: 'Запись скоро будет' }
      : {
          title: 'Доступна запись',
          link: videoLink,
        };

  const tags: CardListProps['tags'] = [];

  tags.push({
    appearance: 'violet',
    text: badge.title,
  });

  if (mainCategory) {
    tags.push({
      text: mainCategory.title,
      appearance: 'violet',
    });
  }

  if (status === EventStatusesEnum.Stop) {
    return tags.reverse();
  }

  return tags;
}

function EventCard({
  cover,
  title,
  slug,
  startsAt,
  category,
  recordLink,
  status,
  isDisabled,
}: EventCardType & { isDisabled?: boolean }) {
  const layoutType = useLayoutType();
  const router = useRouter();

  const link =
    slug === 'gocloud-2025' ? 'https://cloud.ru/gocloud' : `/events/${slug}`;

  const analyticsOptions: ClickAnalyticsType = {
    action: link,
    clickZone: 'body',
    clickElement: 'cardclick',
    clickContent: title,
    uniqueId: `event-card-${title}`,
    transitionType: 'inside-link',
  };

  const { clickAnalytics } = useAnalytics();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    clickAnalytics(analyticsOptions);

    router.push(link);
  };

  return (
    <CardList
      data-test-id="events-card"
      className={s.cardList}
      href={link}
      title={title}
      img={cover}
      tags={getTags({
        status,
        recordLink,
        category,
      })}
      onClick={handleClick}
      labelDate={startsAt}
      layoutType={layoutType}
      disabled={isDisabled}
    />
  );
}

const FIRST_PAST_COUNT = 3;
const GET_PAST_COUNT = 6;

export function EventsPage({
  audience,
  format,
  categories,
  actualEvents,
  pastEvents,
}: EventsProps) {
  const [actual, setActual] = useState(actualEvents);
  const [past, setPast] = useState(pastEvents);
  const [countPast, setCountPast] = useState(FIRST_PAST_COUNT);

  const { clickAnalytics } = useAnalytics();

  const handleShowMore = () => {
    setCountPast((prev) => prev + GET_PAST_COUNT);
  };

  const { watch, reset, setValue } = useForm<DataFromFormInputs>({
    mode: 'onChange',
    defaultValues: {
      categories: [],
      format: [],
      audience: [],
    },
  });

  const handleClearFilters = () => {
    reset({
      categories: [],
      format: [],
      audience: [],
    });
    clickAnalytics({
      action: 'click',
      clickZone: 'body',
      clickElement: 'textclick',
      clickContent: 'Сбросить фильтр',
      uniqueId: 'event-filters',
      transitionType: 'inside-link',
    });
  };

  const layoutType = useLayoutType();

  const selectedCategories = watch('categories') as string[];
  const selectedFormats = watch('format') as string[];
  const selectedAudience = watch('audience') as string[];

  const isDesktop = [LAYOUT_TYPE.Desktop, LAYOUT_TYPE.DesktopSmall]
    .map(String)
    .includes(layoutType);

  const router = useRouter();

  useEffect(() => {
    const handleFilter = (
      events: EventCardType[],
      setState: Dispatch<SetStateAction<EventCardType[]>>,
    ) => {
      const filterEvents = (): EventCardType[] => {
        return events.filter((event) => {
          const categoryMatch =
            (selectedCategories || []).length === 0 ||
            event.category.some((cat) => selectedCategories.includes(cat.slug));
          const formatMatch =
            (selectedFormats || []).length === 0 ||
            selectedFormats.includes(event.format);
          const audienceMatch =
            (selectedAudience || []).length === 0 ||
            selectedAudience.includes(event.audience);

          return categoryMatch && formatMatch && audienceMatch;
        });
      };

      const filteredEvents = filterEvents();

      setState(filteredEvents);
    };

    setCountPast(FIRST_PAST_COUNT);
    handleFilter(actualEvents, setActual);
    handleFilter(pastEvents, setPast);
  }, [selectedCategories, selectedFormats, selectedAudience]);

  const handleSelectValueChange =
    (name: string, value: string[] = []) =>
    (newValue: string[] | undefined = []) => {
      if (!newValue || newValue.length === 0) {
        if (value.length > 1) {
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'filter-select',
            clickContent: 'clear-all-filters',
            uniqueId: `multiselect-${name}`,
            transitionType: 'inside-link',
          });
        } else {
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'filter-select',
            clickContent: value[0],
            uniqueId: `multiselect-${name}`,
            transitionType: 'inside-link',
          });
        }

        // Добавили какой-то фильтр
        if (newValue.length > value.length) {
          clickAnalytics({
            action: 'click',
            clickZone: 'body',
            clickElement: 'filter-select',
            clickContent: newValue[newValue.length - 1],
            uniqueId: `multiselect-${name}`,
            transitionType: 'inside-link',
          });
        } else {
          const removedOption = value.filter(
            (label) => !newValue.includes(label),
          );
          if (removedOption.length !== 0) {
            clickAnalytics({
              action: 'click',
              clickZone: 'body',
              clickElement: 'filter-select',
              clickContent: removedOption[0],
              uniqueId: `multiselect-${name}`,
              transitionType: 'inside-link',
            });
          }
        }
      }

      setValue(name, newValue || []);
    };

  return (
    <Layout>
      <div className={s.root}>
        <HeroZero
          title={HEADER.title}
          description={HEADER.text}
          breadcrumbs={BREADCRUMBS.map((item) => ({
            ...item,
            onClick: item.href
              ? () => {
                  clickAnalytics({
                    action: item.href,
                    clickZone: 'body',
                    clickElement: 'breadcrumb',
                    clickContent: item.label,
                    uniqueId: `breadcrumb-link-${item.label}`,
                    transitionType: 'inside-link',
                  });
                }
              : undefined,
          }))}
          backgroundColor="neutral-background"
        />

        <SectionBasic
          layoutType={layoutType}
          filterItems={[
            {
              id: 'categories',
              label: `Тип мероприятия`,
              placeholder: 'Все',
              options: categories.map((option) => {
                return {
                  value: option.value,
                  option: option.title,
                };
              }),
              defaultValue: undefined,
              selection: 'multiple',
              searchable: false,
              value: selectedCategories,
              onChange: handleSelectValueChange(
                'categories',
                selectedCategories,
              ),
            },
            {
              id: 'format',
              label: 'Формат',
              placeholder: 'Все',
              options: format.map((option) => {
                return {
                  value: option.value,
                  option: option.title,
                };
              }),
              onChange: handleSelectValueChange('format', selectedFormats),
              selection: 'multiple',
              defaultValue: undefined,
              value: selectedFormats,
              searchable: false,
            },
            {
              id: 'audience',
              label: 'Аудитория',
              placeholder: 'Все',
              options: audience.map((option) => {
                return {
                  value: option.value,
                  option: option.title,
                };
              }),
              onChange: handleSelectValueChange('audience', selectedAudience),
              selection: 'multiple',
              defaultValue: undefined,
              searchable: false,
              value: selectedAudience,
            },
          ]}
        >
          {actual.length > 0 && (
            <>
              <Grid
                data-test-id="events-cards-actual-wrapper"
                layoutType={layoutType}
                gap="l"
                columnsConfig={{
                  desktop: {
                    amount: 1,
                    minWidth: 0,
                  },
                }}
              >
                {actual.map((card) => {
                  return <EventCard key={card.id} {...card} />;
                })}
              </Grid>
            </>
          )}

          {actual.length === 0 && past.length === 0 && (
            <InfoBlock
              align="vertical"
              title="Ничего не найдено"
              description="По заданному фильтру мероприятия не найдены."
              size={isDesktop ? 'l' : 'm'}
              footer={
                <ButtonSimple
                  size="m"
                  label="Сбросить фильтр"
                  data-test-id="clear-filters"
                  onClick={handleClearFilters}
                  appearance="primary"
                />
              }
            />
          )}
        </SectionBasic>

        {past.length > 0 && (
          <SectionBasic
            layoutType={layoutType}
            title={PAST_EVENTS.header}
            backgroundColor="neutral-background"
            data-test-id="events-cards-past"
            moreButton={
              past.length >= countPast
                ? {
                    label: 'Показать еще',
                    onClick: () => {
                      handleShowMore();
                      clickAnalytics({
                        action: `click`,
                        clickZone: 'body',
                        clickElement: 'button',
                        clickContent: BUTTON.title,
                        uniqueId: `events-showmore-button-${BUTTON.title}`,
                        transitionType: 'inside-link',
                      });
                    },
                  }
                : undefined
            }
          >
            <Grid
              data-test-id="events-cards-past-wrapper"
              layoutType={layoutType}
              gap="l"
              columnsConfig={{
                desktop: {
                  amount: 1,
                  minWidth: 0,
                },
              }}
            >
              {past.map((card, index) => {
                if (index >= countPast) return null;

                return <EventCard key={card.id} {...card} />;
              })}
            </Grid>
          </SectionBasic>
        )}
      </div>
    </Layout>
  );
}
